import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function ListEditor({ resource, label }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [name, setName] = useState("");

  const load = () => api.get(`/${resource}`).then(r => setItems(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => { setName(edit ? edit.name : ""); }, [edit, open]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    try {
      if (edit) await api.patch(`/${resource}/${edit.id}`, { name });
      else await api.post(`/${resource}`, { name });
      toast.success("Saved");
      setOpen(false); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/${resource}/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{items.length} {label.toLowerCase()}</div>
        <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid={`add-${resource}-btn`}><Plus className="w-4 h-4 mr-1" />New {label.toLowerCase()}</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map(it => (
            <TableRow key={it.id} data-testid={`${resource}-row-${it.id}`}>
              <TableCell className="font-medium">{it.name}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => { setEdit(it); setOpen(true); }}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => del(it.id)}><Trash2 className="w-4 h-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-6">None yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEdit(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{edit ? "Edit" : "New"} {label.toLowerCase()}</DialogTitle></DialogHeader>
          <div><Label>Name *</Label><Input value={name} onChange={e => setName(e.target.value)} autoFocus data-testid={`${resource}-name-input`} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid={`save-${resource}`}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Catalog() {
  return (
    <div className="space-y-5" data-testid="catalog-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Catalog</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Brands & Categories</h1>
      </div>
      <Card className="p-5">
        <Tabs defaultValue="brands">
          <TabsList>
            <TabsTrigger value="brands" data-testid="tab-brands">Brands</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          </TabsList>
          <TabsContent value="brands" className="mt-4"><ListEditor resource="brands" label="Brand" /></TabsContent>
          <TabsContent value="categories" className="mt-4"><ListEditor resource="categories" label="Category" /></TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
